import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHome,
  FaChevronDown,
  FaChevronRight,
  FaChevronUp,
  FaTools,
  FaShoppingCart,
  FaUser,
  FaSignInAlt,
  FaSignOutAlt,
} from 'react-icons/fa';
import { useCart } from '../contexts/CartContext';
import { useTheme } from '../contexts/ThemeContext';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { useAuth } from '../auth/AuthContext';
import { getDisplayNameForUI, getDisplayRole } from '../utils/displayName';

const BottomNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getTotalItems } = useCart();
  const { isDark } = useTheme();
  const { sc } = useThemeClasses();
  const { user, isAuthenticated, logout, getDashboardPath } = useAuth();
  const [activeMenu, setActiveMenu] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const displayName = getDisplayNameForUI(user);
  const currentUser = isAuthenticated && user ? {
    name: displayName || '—',
    avatar: user.avatar || (displayName ? `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random` : null),
    role: user.role || null
  } : null;

  // Animation variants for dropdowns
  const dropdownVariants = {
    hidden: { opacity: 0, y: 10, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2, ease: "easeOut" } },
    exit: { opacity: 0, y: 10, scale: 0.95, transition: { duration: 0.15 } }
  };

  // Navigation menu configuration
  const mainMenu = [
    {
      id: 'home',
      name: 'Home',
      icon: FaHome,
      href: '/',
      submenu: []
    },
    {
      id: 'garages',
      name: 'Garages',
      icon: FaTools,
      href: '/garages',
      submenu: []
    },
    {
      id: 'cart',
      name: 'Cart',
      icon: FaShoppingCart,
      href: '/cart',
      submenu: [],
      badge: getTotalItems() > 0 ? getTotalItems() : null
    },
    {
      id: 'account',
      name: 'Account',
      icon: FaUser,
      href: '/account',
      submenu: [],
    }
  ];

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeMenu !== null && !event.target.closest('.bottom-nav-menu')) {
        setActiveMenu(null);
      }
    };
    if (activeMenu !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeMenu]);

  // Toggle menu handler
  const toggleMenu = useCallback((index) => {
    setActiveMenu(activeMenu === index ? null : index);
  }, [activeMenu]);

  // Handle sign out
  const handleSignOut = useCallback(() => {
    logout();
    setActiveMenu(null);
    navigate('/');
  }, [logout, navigate]);

  const handleViewMyPage = useCallback(
    (e) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      if (!user) {
        navigate('/login');
        setActiveMenu(null);
        return;
      }
      navigate(getDashboardPath(user.role));
      setActiveMenu(null);
    },
    [user, getDashboardPath, navigate]
  );

  // Check if menu item is active
  const isActive = useCallback((item) => {
    if (item.href) {
      return location.pathname === item.href;
    }
    if (item.id === 'account') {
      return location.pathname === '/account' || location.pathname.startsWith('/account/');
    }
    if (item.id === 'garages') {
      return location.pathname === '/garages' || location.pathname.startsWith('/garages/');
    }
    return false;
  }, [location.pathname]);

  // Check if menu item has submenu
  const hasSubmenu = useCallback((item) => {
    return item.submenu && item.submenu.length > 0;
  }, []);

  // Desktop Navigation Item Component
  const DesktopNavItem = ({ item, index }) => {
    const Icon = item.icon;
    const itemHasSubmenu = hasSubmenu(item);
    const isItemActive = isActive(item);

    if (item.href && !itemHasSubmenu) {
      return (
        <Link to={item.href} className="relative bottom-nav-menu">
          <motion.button
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-sparepe font-semibold text-sm transition-all duration-200 ${isItemActive
              ? isDark
                ? 'bg-gradient-to-br from-primary-700 to-primary-500 text-black shadow-sp-glow'
                : 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-md shadow-red-200'
              : isDark
                ? 'sp-bottom-nav-item hover:bg-sparepe-accent-lt/30 hover:text-sparepe-accent-dk'
                : 'text-gray-600 hover:bg-gray-50 hover:text-red-600'
              }`}
            whileHover={{ y: -2, scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon className={`text-sm ${isItemActive ? 'text-white' : ''}`} />
            <span>{item.name}</span>
            {item.badge && (
              <span className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-sm ring-2 ring-white dark:ring-slate-900">
                {item.badge}
              </span>
            )}
          </motion.button>
        </Link>
      );
    }

    return (
      <div className="relative bottom-nav-menu">
        <motion.button
          onClick={() => toggleMenu(index)}
          className={`flex items-center space-x-2 px-5 py-2.5 rounded-sparepe font-semibold text-sm transition-all duration-200 ${activeMenu === index
            ? isDark
              ? 'bg-gradient-to-br from-primary-700 to-primary-500 text-black shadow-sp-glow'
              : 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-md shadow-red-200'
            : isDark
              ? 'sp-bottom-nav-item hover:bg-sparepe-accent-lt/30 hover:text-sparepe-accent-dk'
              : 'text-gray-600 hover:bg-gray-50 hover:text-red-600'
            }`}
          whileHover={{ y: -2, scale: 1.02 }}
          whileTap={{ scale: 0.95 }}
        >
          <Icon className={`text-sm ${activeMenu === index ? 'text-white' : ''}`} />
          <span>{item.name}</span>
          {itemHasSubmenu && (
            <motion.div
              animate={{ rotate: activeMenu === index ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <FaChevronDown className={`text-xs ${activeMenu === index ? 'text-white/80' : ''}`} />
            </motion.div>
          )}
        </motion.button>

        <AnimatePresence>
          {activeMenu === index && itemHasSubmenu && (
            <DesktopDropdown item={item} />
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Desktop / laptop dropdown — same dark Account menu style as mobile
  const DesktopDropdown = ({ item }) => (
    <motion.div
      variants={item.showUserInfo ? dropdownVariants : undefined}
      initial={item.showUserInfo ? "hidden" : { opacity: 0, y: 10 }}
      animate={item.showUserInfo ? "visible" : { opacity: 1, y: 0 }}
      exit={item.showUserInfo ? "exit" : { opacity: 0, y: 10 }}
      transition={item.showUserInfo ? undefined : { duration: 0.2 }}
      className="absolute bottom-full left-1/2 z-40 mb-3 w-72 -translate-x-1/2 overflow-hidden rounded-xl border border-slate-600 bg-[#0f141c] text-slate-100 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-0">
        {item.showUserInfo && currentUser && (
          <div className="mb-0 flex items-center space-x-3 border-b border-slate-700 bg-slate-800/80 px-4 py-3">
            {currentUser.avatar ? (
              <img
                loading="lazy"
                src={currentUser.avatar}
                alt={currentUser.name ?? ''}
                className="h-12 w-12 rounded-sm"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-slate-700 text-lg font-medium text-slate-400">
                —
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-slate-100">
                {currentUser.name ?? '—'}
              </div>
              {currentUser.role ? (
                <div className="text-xs capitalize text-slate-400">{getDisplayRole(user)}</div>
              ) : null}
            </div>
          </div>
        )}
        {item.showUserInfo && !currentUser && (
          <div className="flex items-center space-x-3 border-b border-slate-700 bg-slate-800/80 px-4 py-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-slate-700">
              <FaUser className="text-slate-400" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-100">Guest User</div>
              <div className="text-xs text-slate-400">Please login</div>
            </div>
          </div>
        )}

        <div className="max-h-72 overflow-y-auto scrollbar-hide">
          {item.submenu.map((subItem, subIndex) => (
            <div key={subIndex} className="relative group/sub">
              {subItem.disabled ? (
                <div className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                  {subItem.name.replace(/^— | —$/g, '')}
                </div>
              ) : (
                <Link
                  to={subItem.href || '#'}
                  onClick={() => setActiveMenu(null)}
                  className="flex items-center justify-between border-b border-slate-700/80 px-4 py-3 text-sm font-medium text-slate-200 transition-colors last:border-0 hover:bg-slate-800/90"
                >
                  <span>{subItem.name}</span>
                  {subItem.submenu && <FaChevronRight className="text-xs text-slate-500" />}
                </Link>
              )}

              {subItem.submenu && (
                <div className="invisible absolute top-0 left-full z-50 ml-1 w-52 rounded-lg border border-slate-600 bg-[#0f141c] opacity-0 shadow-lg transition-all duration-300 group-hover/sub:visible group-hover/sub:opacity-100">
                  <div className="py-2">
                    {subItem.submenu.map((nestedItem, nestedIndex) => (
                      <Link
                        key={nestedIndex}
                        to={nestedItem.href}
                        onClick={() => setActiveMenu(null)}
                        className="block px-4 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800/90"
                      >
                        {nestedItem.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {item.showUserInfo && (
          <div className="border-t border-slate-700">
            {currentUser ? (
              <>
                <button
                  type="button"
                  onClick={handleViewMyPage}
                  className="flex w-full items-center justify-center gap-2 py-3 text-sm font-semibold text-sky-400 transition-colors hover:bg-slate-800/80"
                >
                  <FaUser className="text-sm" />
                  <span>View My Page</span>
                </button>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="flex w-full items-center justify-center gap-2 border-t border-slate-700 py-3.5 text-sm font-semibold text-red-400 transition-colors hover:bg-red-950/40"
                >
                  <FaSignOutAlt className="text-sm" />
                  <span>Log out</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setActiveMenu(null)}
                className="flex w-full items-center justify-center gap-2 py-3.5 text-sm font-semibold text-red-400 transition-colors hover:bg-red-950/40"
              >
                <FaSignInAlt className="text-sm" />
                <span>Sign In</span>
              </Link>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );

  // Mobile Navigation Item Component
  const MobileNavItem = ({ item, index }) => {
    const Icon = item.icon;
    const itemHasSubmenu = hasSubmenu(item);
    const isItemActive = isActive(item);
    const isMobileActive = isItemActive || activeMenu === index;

    const navButton = (
      <motion.button
        onClick={itemHasSubmenu ? () => toggleMenu(index) : undefined}
          className={`flex flex-col items-center justify-center px-1 py-1.5 rounded-2xl transition-all duration-200 w-full relative ${isMobileActive
          ? isDark ? 'text-sparepe-accent sp-bottom-nav-item--active' : 'text-red-600'
          : isDark ? 'sp-bottom-nav-item text-sparepe-ink-3' : 'text-gray-500 active:text-red-500'
          }`}
        whileTap={{ scale: 0.85 }}
      >
        <div className={`relative flex items-center justify-center w-10 h-10 rounded-2xl transition-all duration-200 ${isMobileActive
          ? isDark ? 'bg-sparepe-accent-lt/50 shadow-sp-glow' : 'bg-gradient-to-r from-red-50 to-orange-50 shadow-sm'
          : ''
          }`}>
          <Icon className={`text-xl transition-all duration-200 ${isMobileActive ? (isDark ? 'text-sparepe-accent scale-110' : 'text-red-500 scale-110') : isDark ? 'text-sparepe-ink-3' : 'text-gray-500'}`} />
          {item.badge && (
            <span className={`absolute -top-1 -right-1 text-[9px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center shadow-sm ring-2 ${isDark ? 'bg-sparepe-accent text-black ring-sparepe-card' : 'bg-gradient-to-r from-red-500 to-orange-500 text-white ring-white'}`}>
              {item.badge}
            </span>
          )}
        </div>
        <span className={`text-[10px] mt-0.5 font-semibold transition-colors duration-200 ${isMobileActive ? (isDark ? 'text-sparepe-accent' : 'text-red-600') : isDark ? 'text-sparepe-ink-3' : 'text-gray-500'}`}>{item.name}</span>
        {isMobileActive && (
          <motion.div
            layoutId="mobileActiveIndicator"
            className={`absolute -bottom-1 w-5 h-0.5 rounded-full ${isDark ? 'bg-gradient-to-r from-primary-700 to-primary-400' : 'bg-gradient-to-r from-red-500 to-orange-500'}`}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          />
        )}
      </motion.button>
    );

    if (item.href && !itemHasSubmenu) {
      return (
        <Link to={item.href} className="relative bottom-nav-menu flex-1">
          {navButton}
        </Link>
      );
    }

    return (
      <div className="relative bottom-nav-menu flex-1">
        {navButton}
        <AnimatePresence>
          {activeMenu === index && itemHasSubmenu && (
            <MobileDropdown item={item} />
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Mobile Dropdown Component — bottom sheet matching storefront Account menu
  const MobileDropdown = ({ item }) => {
    const Icon = item.icon;
    const showLogout = item.id === 'account' && !!currentUser;

    return (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setActiveMenu(null)}
        />
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-20 left-0 right-0 z-50 flex max-h-[70vh] flex-col rounded-t-3xl border-t border-slate-600 bg-[#0f141c] text-slate-100 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 p-4 pb-0">
            <div className="mb-4 flex items-center justify-between border-b border-slate-700 pb-3">
              <div className="flex items-center gap-2">
                <Icon className="text-amber-400" />
                <h3 className="text-lg font-bold text-white">{item.name}</h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveMenu(null)}
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
              >
                <FaChevronUp />
              </button>
            </div>

            {item.showUserInfo && currentUser && (
              <div className="mb-3 flex items-center space-x-3 rounded-lg bg-slate-800/80 px-4 py-3">
                {currentUser.avatar ? (
                  <img loading="lazy"
                    src={currentUser.avatar}
                    alt={currentUser.name ?? ''}
                    className="h-12 w-12 rounded-sm"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-slate-700 font-medium text-slate-400">—</div>
                )}
                <div>
                  <div className="font-semibold text-slate-100">{currentUser.name ?? '—'}</div>
                  {currentUser.role ? <div className="text-xs capitalize text-slate-400">{getDisplayRole(user)}</div> : null}
                </div>
              </div>
            )}
            {item.showUserInfo && !currentUser && (
              <div className="mb-3 flex items-center space-x-3 rounded-lg bg-slate-800/80 px-4 py-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-slate-700">
                  <FaUser className="text-slate-400" />
                </div>
                <div>
                  <div className="font-semibold text-slate-100">Guest User</div>
                  <div className="text-sm text-slate-400">Please login</div>
                </div>
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4">
            <div className="space-y-1 pb-2">
              {item.submenu.map((subItem, subIndex) => (
                <div key={subIndex}>
                  {subItem.disabled ? (
                    <div className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                      {subItem.name.replace(/^— | —$/g, '')}
                    </div>
                  ) : subItem.submenu ? (
                    <div className="px-4 py-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                      {subItem.name}
                    </div>
                  ) : (
                    <Link
                      to={subItem.href || "#"}
                      onClick={() => setActiveMenu(null)}
                      className="block rounded-lg px-4 py-3 font-medium text-slate-200 transition-colors hover:bg-slate-800/90"
                    >
                      {subItem.name}
                    </Link>
                  )}
                  {subItem.submenu && (
                    <div className="space-y-1 pl-4">
                      {subItem.submenu.map((nestedItem, nestedIndex) => (
                        <Link
                          key={nestedIndex}
                          to={nestedItem.href}
                          onClick={() => setActiveMenu(null)}
                          className="block rounded-lg px-4 py-2.5 text-sm text-slate-300 transition-colors hover:bg-slate-800/90"
                        >
                          {nestedItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {item.showUserInfo && (
            <div className="shrink-0 border-t border-slate-700 bg-[#0f141c] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {currentUser ? (
                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={handleViewMyPage}
                    className="block w-full rounded-lg px-4 py-3 text-left font-medium text-slate-200 transition-colors hover:bg-slate-800/90"
                  >
                    View My Page
                  </button>
                  {showLogout && (
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="block w-full rounded-lg px-4 py-3 text-left font-medium text-slate-200 transition-colors hover:bg-slate-800/90"
                    >
                      Log out
                    </button>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setActiveMenu(null)}
                  className="block rounded-lg px-4 py-3 font-medium text-slate-200 transition-colors hover:bg-slate-800/90"
                >
                  Sign In
                </Link>
              )}
            </div>
          )}
        </motion.div>
      </>
    );
  };

  return (
    <nav
      className={`bottom-nav-scope fixed bottom-0 left-0 right-0 z-50 backdrop-blur-lg ${sc(
        'bottomNav'
      )}`}
    >
      {/* Desktop Navigation */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 py-2.5">
            {mainMenu.map((item, index) => (
              <DesktopNavItem key={item.id} item={item} index={index} />
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="lg:hidden">
        <div className="flex w-full min-w-0 items-stretch justify-around gap-0.5 px-1 py-1 pb-[max(0.25rem,env(safe-area-inset-bottom))]">
          {mainMenu.map((item, index) => (
            <MobileNavItem key={item.id} item={item} index={index} />
          ))}
        </div>
      </div>
    </nav>
  );
};

export default BottomNavigation;
